import { EFinancingProgram } from '@if/core/types'
import { EasyPayWorksheet, EditEasyPayWorksheetDto } from '@src/data/types/EasyPayWorksheetSchema'
import { getApiClient } from '@src/services/api-client'
import { MutationFunction, useMutation, useQueryClient } from '@tanstack/react-query'
import { FullCreditApplication } from '../../types'
import { fullCreditApplicationKeys } from '../full-credit-application/full-credit-application-api'

const worksheetUrl = '/Applications/easypay/{creditApplicationId}/Worksheet'

const buildUrl = (creditApplicationId: string) => worksheetUrl.replace('{creditApplicationId}', creditApplicationId)

export async function createEasyPayWorksheet(data: EditEasyPayWorksheetDto): Promise<EasyPayWorksheet> {
  const apiClient = getApiClient()
  const response = await apiClient.post<EasyPayWorksheet>(buildUrl(data.creditApplicationId), data)
  return response.data
}

export async function updateEasyPayWorksheet(data: EditEasyPayWorksheetDto): Promise<EasyPayWorksheet> {
  const apiClient = getApiClient()
  const response = await apiClient.put<EasyPayWorksheet>(buildUrl(data.creditApplicationId), data)
  return response.data
}

function useSaveEasyPayWorksheet(
  mutationFn: (data: EditEasyPayWorksheetDto) => Promise<EasyPayWorksheet>,
): [MutationFunction<EasyPayWorksheet, EditEasyPayWorksheetDto>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn,
    onSuccess: (savedWorksheet) => {
      const queryKey = fullCreditApplicationKeys.detail(savedWorksheet.creditApplicationId, EFinancingProgram.EasyPay)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          worksheet: savedWorksheet,
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

export function useCreateEasyPayWorksheet(): [MutationFunction<EasyPayWorksheet, EditEasyPayWorksheetDto>, boolean] {
  return useSaveEasyPayWorksheet(createEasyPayWorksheet)
}

export function useUpdateEasyPayWorksheet(): [MutationFunction<EasyPayWorksheet, EditEasyPayWorksheetDto>, boolean] {
  return useSaveEasyPayWorksheet(updateEasyPayWorksheet)
}
